import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Row, Col, Button, FormControl, Table, Spinner, Modal, Form } from "react-bootstrap";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useSortBy,
} from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData } from "../../store/Functions";
import { Container } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AssignDriver = () => {
  // State management
  const [gridData, setGridData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [driverVehicleDetails, setDriverVehicleDetails] = useState([]);
  const [selectedStatusId, setSelectedStatusId] = useState("1");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedDriverId, setSelectedDriverId] = useState("");
  const [loading, setLoading] = useState(true);
  const dispatch = useDispatch();
  const breadCrumbTitle = "Booking Report";
  const breadcrumbItem = "Booking Report";

  const API_URL = `${API_WEB_URLS.MASTER}/0/token/BookingMaster`;
  const API_URL1 = `${API_WEB_URLS.MASTER}/0/token/StatusMaster`;
  const API_URL2 = `${API_WEB_URLS.MASTER}/0/token/DriverVehicleDetails`;
  const API_URL3 = `${API_WEB_URLS.MASTER}/0/token/UpdateVehicleMaster`;

  const fetchTableData = useCallback(
    async (ID) => {
      await Fn_FillListData(dispatch, setGridData, "gridData", `${API_URL}/Id/${ID}`);
    },
    [dispatch, API_URL]
  );

  const driverOptions = useMemo(() => driverVehicleDetails || [], [driverVehicleDetails]);
  const selectedDriverDetails = useMemo(() => {
    if (!selectedDriverId) {
      return undefined;
    }
    return driverOptions.find((driver) => String(driver.Id) === String(selectedDriverId));
  }, [driverOptions, selectedDriverId]);

  const btnAssignDriverOnClick = useCallback(
    (booking) => {
      setSelectedBooking(booking);
      const defaultDriverId =
        driverOptions && driverOptions.length > 0 ? String(driverOptions[0].Id) : "";
      setSelectedDriverId(defaultDriverId);
      setShowAssignModal(true);
    },
    [driverOptions]
  );

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await fetchTableData("1");
        await Fn_FillListData(dispatch, setStatusData, "gridData", `${API_URL1}/Id/0`);
        await Fn_FillListData(dispatch, setDriverVehicleDetails, "gridData", `${API_URL2}/Id/0`);
        setSelectedStatusId("1");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch, API_URL, API_URL1, API_URL2, fetchTableData]);

  const handleStatusFilterChange = useCallback(
    async (event) => {
      const { value } = event.target;
      setSelectedStatusId(value);
      setLoading(true);
      try {
        await fetchTableData(value);
      } finally {
        setLoading(false);
      }
    },
    [fetchTableData]
  );

  const data = useMemo(() => gridData || [], [gridData]);
  const statusOptions = useMemo(() => statusData || [], [statusData]);

  const handleAssignModalClose = useCallback(() => {
    setShowAssignModal(false);
    setSelectedBooking(null);
    setSelectedDriverId("");
  }, []);

  const handleAssignConfirm = useCallback(async () => {
    if (!selectedBooking || !selectedDriverId) {
      return;
    }
console.log(selectedBooking);
    await Fn_FillListData(
      dispatch,
      setGridData,
      "gridData",
      `${API_URL3}/${selectedBooking.Id}/${selectedDriverId}`
    );

    await fetchTableData(selectedStatusId);

    setShowAssignModal(false);
    setSelectedBooking(null);
    setSelectedDriverId("");
  }, [API_URL3, dispatch, fetchTableData, selectedBooking, selectedDriverId, selectedStatusId]);

  const columns = useMemo(
    () => [
      {
        Header: "Pickup Address",
        accessor: "PickupAddress",
        Cell: ({ value }) => (
          <div className="text-wrap" style={{ minWidth: "220px" }}>
            <i className="fas fa-map-marker-alt me-2 text-primary"></i>
            <span className="fw-semibold">{value || "-"}</span>
          </div>
        ),
      },
      {
        Header: "Drop Address",
        accessor: "DropAddress",
        Cell: ({ value }) => (
          <div className="text-wrap" style={{ minWidth: "220px" }}>
            <i className="fas fa-flag-checkered me-2 text-success"></i>
            <span className="fw-semibold">{value || "-"}</span>
          </div>
        ),
      },
      {
        Header: "Pickup Latitude",
        accessor: "PickupLatitude",
        Cell: ({ value }) => <span className="text-muted">{value || "-"}</span>,
      },
      {
        Header: "Pickup Longitude",
        accessor: "PickupLongitude",
        Cell: ({ value }) => <span className="text-muted">{value || "-"}</span>,
      },
      {
        Header: "Drop Latitude",
        accessor: "DropLatitude",
        Cell: ({ value }) => <span className="text-muted">{value || "-"}</span>,
      },
      {
        Header: "Drop Longitude",
        accessor: "DropLongitude",
        Cell: ({ value }) => <span className="text-muted">{value || "-"}</span>,
      },
      {
        Header: "User Name",
        accessor: "UserName",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold d-block">{row.original.UserName || "-"}</span>
            <small className="text-muted">{row.original.UserMobileNo || "-"}</small>
          </div>
        ),
      },
      {
        Header: "OTP",
        accessor: "OTP",
        Cell: ({ value }) => (
          <span className="badge bg-info text-dark fs-6">
            {value ? value.toString().padStart(4, "0") : "-"}
          </span>
        ),
      },
      {
        Header: "Driver Name",
        accessor: "DriverName",
        Cell: ({ row }) => (
          <div>
            <span className="fw-semibold d-block">{row.original.DriverName || "-"}</span>
            <small className="text-muted">{row.original.DriverMobileNo || "-"}</small>
          </div>
        ),
      },
      {
        Header: "Vehicle Name",
        accessor: "VehicleName",
        Cell: ({ value }) => (
          <div className="d-flex align-items-center">
            <i className="bx bx-car me-2 text-primary" style={{ fontSize: "1.3rem" }}></i>
            <span className="fw-semibold text-uppercase">{value || "-"}</span>
          </div>
        ),
      },
      {
        Header: "Vehicle Category",
        accessor: "VehicleCategory",
        Cell: ({ value }) => (
          <span className="badge bg-danger bg-opacity-10 text-danger fw-semibold">
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "Status",
        Cell: ({ value }) => (
          <span
            className={`badge ${value === "Completed" ? "bg-success" : value === "Pending" ? "bg-warning text-dark" : "bg-light text-dark"}`}
          >
            {value || "-"}
          </span>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const { F_StatusMaster } = row.original;
          const isPendingAssignment =
            F_StatusMaster !== undefined &&
            F_StatusMaster !== null &&
            String(F_StatusMaster) === "1";

          return (
            <div className="d-flex justify-content-center">
              {isPendingAssignment ? (
                <Button
                  className="master-list-assign-btn"
                  onClick={() => btnAssignDriverOnClick(row.original)}
                  size="sm"
                >
                  Assign the Driver
                </Button>
              ) : (
                <span className="text-muted small">Driver assigned</span>
              )}
            </div>
          );
        },
      },
    ],
    [btnAssignDriverOnClick]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: { 
        pageIndex: 0, 
        pageSize: 10,
        sortBy: [
          {
            id: "PickupAddress",
            desc: false
          }
        ]
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  if (loading) {
    return (
      <div className="master-list-loading d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-muted">Loading bookings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="master-list-container">
      <Container fluid={true} className="px-3 px-md-4">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />

        {/* Controls Section */}
        <div className="master-list-controls master-list-slide-in mb-4">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="search-wrapper">
                <FormControl
                  type="text"
                  placeholder="Search booking by pickup, drop, customer..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="form-control-lg"
                />
              </div>
            </Col>
            <Col md={6} sm={12}>
              <div className="d-flex align-items-center gap-3">
                <span className="fw-semibold text-muted">Filter by status:</span>
                <select
                  className="form-select form-select-lg"
                  value={selectedStatusId}
                  onChange={handleStatusFilterChange}
                >
                  {statusOptions.map((status) => (
                    <option key={status.Id} value={status.Id}>
                      {status.Name}
                    </option>
                  ))}
                </select>
              </div>
            </Col>
          </Row>
        </div>

        {/* Table Section */}
        <div className="master-list-table-container master-list-bounce-in mb-4">
          <div className="table-responsive">
            <Table {...getTableProps()} className="master-list-table table-hover">
              <thead className="custom-header">
                {headerGroups.map((headerGroup, headerGroupIndex) => (
                  <tr {...headerGroup.getHeaderGroupProps()} key={headerGroupIndex}>
                    {headerGroup.headers.map((column, columnIndex) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        key={columnIndex}
                        className="align-middle"
                        style={{ 
                          backgroundColor: '#6D68CB',
                          color: 'white',
                          borderColor: '#6D68CB'
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <span className="fw-semibold">{column.render("Header")}</span>
                          <span className="sort-indicator">
                            {column.isSorted
                              ? column.isSortedDesc
                                ? <i className="fas fa-sort-down" style={{ color: 'white' }}></i>
                                : <i className="fas fa-sort-up" style={{ color: 'white' }}></i>
                              : <i className="fas fa-sort" style={{ color: 'rgba(255,255,255,0.7)' }}></i>}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row, rowIndex) => {
                  prepareRow(row);
                  return (
                    <tr {...row.getRowProps()} key={rowIndex} className="align-middle">
                      {row.cells.map((cell, cellIndex) => (
                        <td {...cell.getCellProps()} key={cellIndex} className="py-3">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination Section */}
        <div className="master-list-pagination">
          <Row className="align-items-center">
            <Col md={6} sm={12} className="mb-3 mb-md-0">
              <div className="master-list-pagination-info d-flex flex-wrap align-items-center gap-3">
                <span className="text-muted">
                  Page <strong>{pageIndex + 1}</strong> of <strong>{pageOptions.length}</strong>
                </span>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Go to:</span>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    style={{ width: '80px' }}
                    defaultValue={pageIndex + 1}
                    onChange={(e) => {
                      const page = e.target.value ? Number(e.target.value) - 1 : 0;
                      gotoPage(page);
                    }}
                  />
                </div>
                <div className="d-flex align-items-center gap-2">
                  <span className="text-muted">Show:</span>
                  <select
                    className="form-select form-select-sm"
                    style={{ width: '80px' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                    }}
                  >
                    {[10, 20, 30, 40, 50].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Col>
            <Col md={6} sm={12} className="text-md-end">
              <div className="master-list-pagination-controls d-flex gap-2 justify-content-md-end">
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(0)} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => previousPage()} 
                  disabled={!canPreviousPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-left"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => nextPage()} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-right"></i>
                </Button>
                <Button 
                  className="pagination-btn" 
                  onClick={() => gotoPage(pageCount - 1)} 
                  disabled={!canNextPage}
                  size="sm"
                  variant="outline-secondary"
                >
                  <i className="fas fa-angle-double-right"></i>
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </Container>

      <Modal
        show={showAssignModal}
        onHide={handleAssignModalClose}
        centered
        size="lg"
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Assign Driver</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking ? (
            <>
              <div className="mb-4">
                <h6 className="fw-semibold mb-2">Booking details</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Pickup</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.PickupAddress || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        Lat: {selectedBooking.PickupLatitude || "-"} | Lng:{" "}
                        {selectedBooking.PickupLongitude || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Drop</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.DropAddress || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        Lat: {selectedBooking.DropLatitude || "-"} | Lng:{" "}
                        {selectedBooking.DropLongitude || "-"}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Customer</span>
                      <div className="fw-semibold mt-1">
                        {selectedBooking.UserName || "-"}
                      </div>
                      <div className="text-muted small mt-2">
                        {selectedBooking.UserMobileNo || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100">
                      <span className="text-muted text-uppercase small">Current status</span>
                      <div className="fw-semibold mt-1">{selectedBooking.Status || "-"}</div>
                      <div className="text-muted small mt-2">
                        OTP: {selectedBooking.OTP || "-"}
                      </div>
                    </div>
                  </Col>
                </Row>
                <Row className="g-3 mt-1">
                  <Col md={6}>
                    <div className="border rounded p-3 h-100 border-danger bg-danger bg-opacity-10 text-danger">
                      <span className="text-uppercase small fw-semibold">Vehicle category</span>
                      <div className="fw-bold mt-1">
                        {selectedBooking.VehicleCategory || "-"}
                      </div>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="border rounded p-3 h-100 border-danger bg-danger bg-opacity-10 text-danger">
                      <span className="text-uppercase small fw-semibold">Weight range</span>
                      <div className="fw-bold mt-1">
                        {selectedBooking.WeightRangeFrom ?? "-"} kg to{" "}
                        {selectedBooking.WeightRangeTo ?? "-"} kg
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              <Form.Group controlId="assignDriverSelect" className="mb-3">
                <Form.Label className="fw-semibold">Select driver & vehicle</Form.Label>
                <Form.Select
                  size="lg"
                  value={selectedDriverId}
                  onChange={(event) => setSelectedDriverId(event.target.value)}
                  disabled={!driverOptions.length}
                >
                  {!driverOptions.length && <option value="">No drivers available</option>}
                  {driverOptions.map((driver) => (
                    <option key={driver.Id} value={String(driver.Id)}>
                      {driver.DriverName} · {driver.VehicleRegistrationNo} ·{" "}
                      {driver.VehicleTypeName}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {selectedDriverDetails && (
                <div className="border rounded p-3 bg-light">
                  <h6 className="fw-semibold mb-2">Driver & vehicle details</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Driver</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.DriverName || "-"}
                        </div>
                        <div className="text-muted small">
                          {selectedDriverDetails.DriverMobileNo || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Vehicle</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.VehicleRegistrationNo || "-"}
                        </div>
                        <div className="text-muted small">
                          {selectedDriverDetails.VehicleTypeName || "-"}
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row className="g-3 mt-1">
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Vehicle No</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.VehicleNo || "-"}
                        </div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div>
                        <span className="text-muted text-uppercase small">Capacity</span>
                        <div className="fw-semibold mt-1">
                          {selectedDriverDetails.WeightCapacity ?? "-"} kg
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}

              {!driverOptions.length && (
                <div className="text-center text-muted py-4 border rounded">
                  No driver & vehicle combinations are available right now.
                </div>
              )}
            </>
          ) : (
            <div className="text-center text-muted py-4">No booking selected.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleAssignModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleAssignConfirm}
            disabled={!selectedBooking || !selectedDriverId}
          >
            Confirm Assignment
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AssignDriver;
